import { useContext } from 'react';
import { AppRoutes, TaskSkill } from 'src/types';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import { SkillLabel } from 'src/components/SkillLabel';
import {
  useBreakpoint,
  useChatHistory,
  useGetUser,
  useMigrationToSNModalActions,
  useSubmitUserInput,
} from 'src/hooks';
import { SkillLabelIcon } from 'src/components/SkillLabelIcon';
import styles from './PillarsBlendTile.module.scss';
import { BetaLabel } from 'src/v2/commonComponents/BetaLabel';
import { RecommendedSkillExample } from 'src/types/recommendedSkills';
import AuthContext from 'src/contexts/AuthContext';
import { GUEST_ACCESS_TASK_INPUT_VALUE } from 'src/constants';
import { useFeatureFlagEnabled } from 'posthog-js/react';

type PillarsBlendTileProps = {
  skill: TaskSkill | string;
  template?: RecommendedSkillExample;
  onShowUpsell: () => void;
};

export const PillarsBlendTile = ({
  skill,
  template,
  onShowUpsell,
}: PillarsBlendTileProps) => {
  const { isGuestAccess } = useContext(AuthContext);
  const { isNinjaRetired } = useChatHistory();
  const { setThreadInputBoxValue, onCheckInputTagReplacement } = useContext(
    ThreadInputBoxContext,
  );
  const { changeShouldShowMigrationToSNModal } = useMigrationToSNModalActions();
  const disableRequestsInMNForFreeTier = useFeatureFlagEnabled(
    'disableRequestsInMNForFreeTier',
  );

  const { isDesktopAndUp } = useBreakpoint();
  const { onSubmitUserInput } = useSubmitUserInput();
  // TODO(olha): waiting for flag from BE
  const {
    isTeamsUltraTier,
    isEnterpriseTier,
    isUltraTier,
    isUltraTrialTier,
    isEnterpriseTrialTier,
    isSuperNinjaUltraTier,
    isSuperNinjaUltraTrialTier,
    isTeamsMaxTier,
    isMaxTier,
    isFreeTier,
    isStarterTier,
  } = useGetUser();

  const isVideoCapable =
    isUltraTier ||
    isUltraTrialTier ||
    isEnterpriseTrialTier ||
    isEnterpriseTier ||
    isTeamsUltraTier ||
    isSuperNinjaUltraTier ||
    isSuperNinjaUltraTrialTier ||
    isTeamsMaxTier ||
    isMaxTier;

  const isVideoSkill = skill === 'video';

  const handleTileClicktoMigration = () => {
    changeShouldShowMigrationToSNModal(true);
    return;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTileClick = () => {
    if (!template) {
      return;
    }

    if (isVideoSkill && !isVideoCapable) {
      if (
        (isFreeTier || isStarterTier || isNinjaRetired) &&
        disableRequestsInMNForFreeTier
      ) {
        changeShouldShowMigrationToSNModal(true);
      } else {
        onShowUpsell();
      }
      return;
    }

    const inputValue = `/${template.command} ${template.inputText}`;

    if (isGuestAccess) {
      // save input value to local storage
      localStorage.setItem(GUEST_ACCESS_TASK_INPUT_VALUE, inputValue);
      window.location.href = AppRoutes.LOGIN;
      return;
    }

    setThreadInputBoxValue(inputValue);

    const shouldShowReplaceTooltip = onCheckInputTagReplacement(inputValue);

    if (!shouldShowReplaceTooltip) {
      onSubmitUserInput({
        userInput: inputValue,
        customParams: {
          shouldIgnoreThinkMode: true,
        },
      });
    }
  };

  return (
    <div
      className="nj-random-pillars--blend-tile"
      onClick={handleTileClicktoMigration}
      data-e2e={`skill-example-button-${skill}`}
    >
      <div className={styles.wrapper}>
        {isDesktopAndUp && <SkillLabelIcon skill={skill} />}
        <SkillLabel skill={skill} />
      </div>

      <span className="nj-random-pillars--blend-tile--content">
        {template?.displayMessage}
      </span>

      {isVideoSkill && <BetaLabel isAbsolute className={styles.label} />}
    </div>
  );
};
